import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { HttpClient } from '../../../services/api/client'
import { TokenManager } from '../../../services/auth/tokenManager'

export interface VentureEvidenceCounts {
  hasEvidence: boolean
  surveyCount: number
  interviewCount: number
  documentCount: number
}

const DEFAULT_COUNTS: VentureEvidenceCounts = {
  hasEvidence: false,
  surveyCount: 0,
  interviewCount: 0,
  documentCount: 0,
}

/**
 * Checks whether a venture node has any research evidence (surveys, interviews, or uploaded docs).
 * Used to enable/disable the "Synthesize from research" button.
 * Re-fetches when ventureNodeId or mindmapId changes.
 */
export function useVentureEvidence(
  ventureNodeId: string | null | undefined,
  mindmapId: string | undefined
): VentureEvidenceCounts & { loading: boolean } {
  const [counts, setCounts] = useState<VentureEvidenceCounts>(DEFAULT_COUNTS)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    const cleanup = (): void => {
      cancelled = true
    }

    if (!ventureNodeId || !mindmapId) {
      setCounts(DEFAULT_COUNTS)
      return cleanup
    }

    const fetchCounts = async (): Promise<void> => {
      setLoading(true)
      try {
        const headers = await TokenManager.getAuthHeaders()
        const result = await HttpClient.get<VentureEvidenceCounts>(
          `/mindmaps/${mindmapId}/ventures/${ventureNodeId}/has-evidence`,
          headers
        )
        if (!cancelled) {
          setCounts(result.data ?? DEFAULT_COUNTS)
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'mindmap', action: 'fetch_venture_evidence' },
        })
        if (!cancelled) {
          setCounts(DEFAULT_COUNTS)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void fetchCounts()

    return cleanup
  }, [ventureNodeId, mindmapId])

  return { ...counts, loading }
}
